import React, { useCallback, useMemo, useState } from 'react';
import { StyleSheet } from 'react-native';

import { PlayerProjectionFragment } from '@/api/events/query.generated';
import { EventInfo } from '@/api/events/types/types';
import LockIcon from '@/assets/icons/lockNew';
import { Button } from '@/components/ButtonComponent';
import { AnimatedPressableOpacity } from '@/components/animated-pressable-opacity/AnimatedPressableOpacity';
import { Box, Row } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { TileProfileImage } from '@/components/player-profile/tiles/TileProfileImage';
import { borderWidth, tilePaddingTop } from '@/components/player-profile/tiles/utils';
import BetrAnalytics from '@/feature/analytics/analytics';
import { AnalyticsEvent } from '@/feature/analytics/constants';
import { betslipPlayerSelector, useBetslipStore } from '@/feature/betslip-pickem/hooks/use-betslip-store';
import { leagueConfigSelector, useLeagueConfigsStore } from '@/feature/betslip-pickem/hooks/use-league-configs-store';
import { PlayerWithTeam } from '@/feature/betslip-pickem/types';
import { usePlayerProfileDataStore } from '@/feature/player-profile-sheets/hooks/use-player-profile-data-store';
import { MarketStatus, ProjectionType } from '@/types/api.generated';
import { getPlayerJerseyNumber } from '@/utils/formatPlayerInfo';
import { getPlayerLogoDetails } from '@/utils/get-player-logo-details';
import { isSameProjection } from '@/utils/projections';
import { sortProjections } from '@/utils/sortProjections';
import { useShallow } from 'zustand/react/shallow';

import { PickSelectionMethods } from '../../hooks/use-pick-selection';
import { usePlayerFiltersState } from '../../hooks/use-player-filters';
import { POPULAR_PLAYERS_FILTER } from '../../utils/players';
import { PickButtonsWithFunctionality } from '../PickProjectionButtonsSection';
import { PlayerDetailsTile } from '../PlayerDetails';
import { ProjectionDescriptionTile } from './ProjectionDescriptionTile';

type PlayerTileSpecificProps = {
    event: EventInfo;
    testID?: string;
    analyticsTag?: string;
    player: PlayerWithTeam;
    containerWidth: number;
    playerProjections: PlayerProjectionFragment[];
    hideGameTime?: boolean;
    openPlayerProfile: (playerId: string, eventId: string) => void;
} & Partial<PickSelectionMethods>;

/**
 * Projection card tile used to toggle between different projection types.
 * @param {PlayerTileSpecificProps} props
 * @returns {React.JSX.Element}
 */
export const PlayerTile = (props: PlayerTileSpecificProps) => {
    const { player, event, testID, analyticsTag, openPlayerProfile, containerWidth, playerProjections, hideGameTime } =
        props;

    const team = player.team;
    const league = event?.league || team?.league || player.league;
    const { leagueColor, leagueIcon } = useLeagueConfigsStore(useShallow(leagueConfigSelector(league)));

    const { teamLogo, logoText } = getPlayerLogoDetails(player, league, team);
    const playerNumber = getPlayerJerseyNumber(league, player?.jerseyNumber);

    const { playerProjectionFilter } = usePlayerFiltersState();

    const playerPickedEntry = useBetslipStore(
        betslipPlayerSelector({
            eventId: event.id,
            playerId: player.id,
        })
    );
    const setPopularPlayerProjection = usePlayerProfileDataStore(state => state.actions.setPopularPlayerProjection);

    const projectionFilter = useMemo(() => {
        if (playerProjectionFilter === POPULAR_PLAYERS_FILTER) {
            const nonSuspended = playerProjections
                .filter(it => it.marketStatus !== MarketStatus.Suspended)
                .sort(sortProjections);
            if (nonSuspended.length === 0) {
                //If all projections are suspended, just fetch the first one
                return playerProjections.sort(sortProjections)?.[0]?.key;
            }
            //Otherwise, randomly pick a random projection, from the top 6 projections
            //This ensures that not all players on the 'Popular' tab will have the same projection selected
            const randomIdx = hashIds(event?.id ?? '', player.id, Math.min(nonSuspended.length, 6));
            return nonSuspended[randomIdx]?.key;
        } else {
            return playerProjectionFilter;
        }
    }, [event?.id, player.id, playerProjectionFilter, playerProjections]);

    //All the projections for this player, for the selected projection filter
    const filteredProjections = useMemo(() => {
        const projectionsByFilter = playerProjections.filter(it => it.key === projectionFilter);
        const allProjectionsByFilterSuspended = projectionsByFilter.every(
            it => it.marketStatus === MarketStatus.Suspended
        );
        // Conditionally filter out suspended markets if not all filtered markets are suspended
        // If all are suspended, we want to show the suspended state for the entire tile
        let computedProjections = allProjectionsByFilterSuspended
            ? projectionsByFilter
            : projectionsByFilter.filter(it => it.marketStatus === MarketStatus.Opened);

        return computedProjections.sort(sortProjections);
    }, [projectionFilter, playerProjections]);

    // Group projections by value, for example:
    // value 20 - [proj1]
    // value 25 - [proj2, proj3]
    // value 30 - [proj4]
    const projectionsByValue: { value: number; projections: PlayerProjectionFragment[] }[] = useMemo(() => {
        return filteredProjections
            .reduce((acc: { value: number; projections: PlayerProjectionFragment[] }[], projection) => {
                const value = projection.nonRegularValue || projection.value;
                const existing = acc.find(it => it.value === value);
                if (projection.marketStatus === MarketStatus.Suspended) {
                    //Skip suspended projections
                    return acc;
                }
                if (existing) {
                    existing.projections.push(projection);
                } else {
                    acc.push({ value, projections: [projection] });
                }
                return acc;
            }, [])
            .sort((a, b) => a.value - b.value);
    }, [filteredProjections]);

    //Index to toggle between same value projections (20 REGULAR LESS, 20 BOOST MORE)
    const [projectionIndex, setProjectionIndex] = useState<number>(0);
    //Index to toggle between projection types edge1, edge2, boosts etc.
    const [projectionTypeIndex, setProjectionTypeIndex] = useState<number>();

    //Computes the default projection type index (index of regular projection or picked projection value)
    const computeDefaultProjectionTypeIndex = useCallback((): number => {
        //First check if the player has a picked value for this projection, and select the index of that projection
        if (playerPickedEntry) {
            const pickedIdx = projectionsByValue.findIndex(group =>
                group.projections.some(
                    p => p.key === playerPickedEntry.projection.key && p.type === playerPickedEntry.projection.type
                )
            );
            if (pickedIdx >= 0) {
                return pickedIdx;
            }
        }
        //Otherwise try to find the index of the regular projection
        const regularProjIdx = projectionsByValue.findIndex(it =>
            it.projections.find(p => p.type === ProjectionType.Regular)
        );
        if (regularProjIdx >= 0) {
            return regularProjIdx;
        }
        //If no regular projection, just return the first index
        return 0;
    }, [playerPickedEntry, projectionsByValue]);

    //The computed projection type index
    // - if 'projectionTypeIndex' is not defined fallback to the default computed index (index of regular projection or picked projection value)
    // - if 'projectionTypeIndex' is defined, this means user toggled projection type via +/- so use that index
    const computedProjectionTypeIndex = useMemo(() => {
        if (projectionTypeIndex !== undefined) {
            return projectionTypeIndex;
        }
        return computeDefaultProjectionTypeIndex();
    }, [computeDefaultProjectionTypeIndex, projectionTypeIndex]);

    const handleToggleProjection = useCallback(
        (direction: 'prev' | 'next') => {
            if (direction === 'next') {
                setProjectionTypeIndex(index => {
                    if (index === undefined) {
                        return computeDefaultProjectionTypeIndex() + 1;
                    }
                    return index + 1;
                });
            } else {
                setProjectionTypeIndex(index => {
                    if (index === undefined) {
                        return computeDefaultProjectionTypeIndex() - 1;
                    }
                    return index - 1;
                });
            }
            setProjectionIndex(0);
        },
        [computeDefaultProjectionTypeIndex]
    );

    const selectedProjectionsByValue = useMemo(
        () => projectionsByValue[computedProjectionTypeIndex],
        [projectionsByValue, computedProjectionTypeIndex]
    );

    // If there are no projections for the current value (if all suspended - as those are filtered),
    // we fallback to the first one, as this will allow us to display the suspended state for the tile
    // This can still be undefined in rare cases (when selected projection is removed by a WS update in LIVE games),
    // in which case we are supposed to show the suspended tile
    const projection = selectedProjectionsByValue?.projections[projectionIndex] ?? filteredProjections[0];

    const hasNextProjectionsByValue = computedProjectionTypeIndex < projectionsByValue.length - 1;
    const hasPreviousProjectionsByValue = computedProjectionTypeIndex > 0;

    const isThisProjectionPicked = playerPickedEntry && isSameProjection(playerPickedEntry.projection, projection);
    const allFilteredProjectionsSuspended =
        filteredProjections.length > 0 && filteredProjections.every(p => p.marketStatus === MarketStatus.Suspended);

    const openPlayerCard = useCallback(() => {
        openPlayerProfile(player.id, event.id);
        if (playerProjectionFilter === POPULAR_PLAYERS_FILTER) {
            setPopularPlayerProjection(projectionFilter);
        }

        if (analyticsTag) {
            BetrAnalytics.trackEvent(AnalyticsEvent.EXPAND_PLAYER_PROPS, {
                location: analyticsTag,
            });
        }
    }, [
        analyticsTag,
        event.id,
        openPlayerProfile,
        player.id,
        playerProjectionFilter,
        projectionFilter,
        setPopularPlayerProjection,
    ]);

    const tileProps = useMemo(() => {
        return isThisProjectionPicked
            ? { mode: 'selection' as const, projection, outcome: playerPickedEntry?.outcome }
            : { mode: 'highlight' as const, projection };
    }, [isThisProjectionPicked, playerPickedEntry?.outcome, projection]);

    const handleSwapProjection = useCallback(() => {
        const numberOfProjections = selectedProjectionsByValue?.projections.length || 0;
        setProjectionIndex(prevIndex => (prevIndex + 1) % numberOfProjections);
    }, [selectedProjectionsByValue]);

    return (
        <AnimatedPressableOpacity onPress={openPlayerCard} accessible={false} flex={1}>
            <Box
                style={[
                    styles.container,
                    {
                        paddingTop: tilePaddingTop,
                    },
                ]}
            >
                <TileProfileImage
                    key={`${player.firstName}-${player.lastName}-${player.id}`}
                    playerImageUrl={player.icon}
                    teamImageUrl={teamLogo ?? leagueIcon}
                    teamColor={team?.color ?? leagueColor}
                    bgColor={ds.colorToken.background.neutral.subtler}
                    playerNumber={playerNumber}
                    containerWidth={containerWidth}
                    selectedProjection={projection}
                    event={event}
                    player={player}
                    secondaryColor={team?.secondaryColor}
                    logoText={logoText}
                />
                <Box style={styles.playerDetailsContainer}>
                    <PlayerDetailsTile
                        isSuspended={allFilteredProjectionsSuspended}
                        projections={playerProjections}
                        player={player}
                        event={event}
                        isVoided={false}
                        teamName={team?.name || ''}
                        selectedProjection={projection}
                        testID={`playerEventDetails-${testID}`}
                        hideGameTime={hideGameTime}
                    />
                </Box>
                <Box flex={1} />
                <Row style={styles.projectionDescriptionContainer}>
                    {projection ? (
                        <ProjectionDescriptionTile
                            selectedProjection={projection}
                            isSuspended={allFilteredProjectionsSuspended}
                            handleToggleProjection={handleToggleProjection}
                            hasNextProjection={hasNextProjectionsByValue}
                            hasPreviousProjection={hasPreviousProjectionsByValue}
                        />
                    ) : null}
                </Row>
                <Box>
                    {!allFilteredProjectionsSuspended && projection ? (
                        <PickButtonsWithFunctionality
                            testID={testID}
                            {...props}
                            leagueIcon={leagueIcon}
                            leagueColor={leagueColor}
                            selectedProjection={projection}
                            selectedProjectionsByValue={selectedProjectionsByValue}
                            swapProjection={handleSwapProjection}
                            viewMode={'tile'}
                            containerWidth={containerWidth}
                            {...tileProps}
                        />
                    ) : (
                        <Button
                            testID="playerTileLockButton"
                            disabled
                            label={<LockIcon />}
                            style={styles.suspendedButton}
                        />
                    )}
                </Box>
            </Box>
        </AnimatedPressableOpacity>
    );
};

/**
 * Hash function that generates a number between 0-max from two string IDs
 * @param id1 First ID string (format like "682bfe517fd44f648eebff1c")
 * @param id2 Second ID string (format like "682bfe517fd44f648eebff1c")
 * @returns number from 0-max
 */
function hashIds(id1: string, id2: string, max: number): number {
    // Combine both IDs
    const combined = id1 + id2;

    let hash = 0;
    for (let i = 0; i < combined.length; i++) {
        // hash * 33 + char code (ensures good distribution for strings)
        // eslint-disable-next-line no-bitwise
        hash = (hash << 5) + hash + combined.charCodeAt(i);
    }
    // Map to range 0-max
    const result = Math.abs(hash) % max;
    return result;
}

const styles = StyleSheet.create({
    playerDetailsContainer: {
        marginTop: 57,
        marginBottom: 8,
        zIndex: 1,
    },
    projectionDescriptionContainer: {
        marginBottom: 14,
    },
    container: {
        borderColor: ds.colorValue.border.subtle,
        borderRadius: 16,
        paddingBottom: 16,
        paddingHorizontal: 16,
        borderWidth: borderWidth,
        backgroundColor: ds.colorValue.background.neutral.subtler,
        borderCurve: 'continuous',
        flexGrow: 1,
        overflow: 'hidden',
    },
    suspendedButton: {
        padding: 10,
        borderRadius: 10,
        backgroundColor: ds.colorValue.background.neutral.subtle,
        justifyContent: 'center',
        alignItems: 'center',
        borderCurve: 'continuous',
        borderWidth: 0,
    },
});
